import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BACKDROP_MODES,
  LOOK_LIMITS,
  LOOK_PRESETS,
  MAX_BACKDROP_BLUR,
  MAX_BACKDROP_DIM,
  defaultCameraLook,
  type CameraLook,
  type ClientMessage,
  type EventSummary,
  type StageLook,
  type StreamSource,
} from '@rabbitfuel/shared';
import { HelpLink } from './DirectorHelp.tsx';

/**
 * Pult fuer das Aussehen des Hauptbilds: Hintergrund hinter dem Kamerabild
 * und Bildkorrektur je Kamera.
 *
 * Beide Zustaende liegen auf dem Server, nicht in diesem Tab -- deshalb
 * ueberleben sie einen Reload der Regie, gelten fuer alle Zuschauer und
 * landen ohne Zutun im RTMP-Ausgang.
 */
export function LookDesk({
  stage,
  cameras,
  sources,
  activeSourceId,
  event,
  send,
}: {
  stage: StageLook;
  cameras: Record<string, CameraLook>;
  /** Freigegebene Quellen -- nur die lassen sich korrigieren. */
  sources: StreamSource[];
  activeSourceId: string | null;
  event: EventSummary | null;
  send: (msg: ClientMessage) => void;
}) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const throttled = useThrottledSend(send);

  // Ohne eigene Wahl gilt die Kamera im Hauptbild: die will man korrigieren.
  const selectedId = useMemo(() => {
    if (picked && sources.some((s) => s.id === picked)) return picked;
    if (activeSourceId && sources.some((s) => s.id === activeSourceId)) return activeSourceId;
    return sources[0]?.id ?? null;
  }, [picked, activeSourceId, sources]);

  const selected = sources.find((s) => s.id === selectedId) ?? null;
  const look = (selectedId ? cameras[selectedId] : undefined) ?? defaultCameraLook();

  const patchCamera = (patch: Partial<CameraLook>, live = false) => {
    if (!selectedId) return;
    const msg: ClientMessage = { t: 'look:camera', sourceId: selectedId, patch };
    if (live) throttled(msg);
    else send(msg);
  };

  const backdropMissing =
    (stage.backdrop === 'cover' && !event?.coverUrl) || (stage.backdrop === 'image' && !event?.backdropUrl);

  return (
    <section className={`deck${open ? '' : ' is-collapsed'}`}>
      <header className="deck__head">
        <button type="button" className="deck__toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? '▾' : '▸'} Hintergrund &amp; Bild
        </button>
        <HelpLink topic="look" />
        <span className="deck__mode">{BACKDROP_MODES.find((m) => m.id === stage.backdrop)?.label}</span>
      </header>

      {open && (
        <div className="deck__body">
          <div className="deck__block">
            <div className="deck__label">Hintergrund</div>
            <div className="chip-row">
              {BACKDROP_MODES.map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  className={`chip${stage.backdrop === mode.id ? ' is-active' : ''}`}
                  title={mode.hint}
                  onClick={() => send({ t: 'look:stage', patch: { backdrop: mode.id } })}
                >
                  {mode.label}
                </button>
              ))}
            </div>
            {backdropMissing && (
              <div className="notice notice--stage">
                Für dieses Event ist {stage.backdrop === 'cover' ? 'kein Titelbild' : 'kein Hintergrundbild'}{' '}
                hinterlegt — bis dahin bleibt es schwarz. Hochladen unter Event → Einstellungen.
              </div>
            )}

            <div className="deck__label">Kamerabild im Rahmen</div>
            <div className="chip-row">
              {(
                [
                  ['contain', 'Ganz zeigen', 'Bild vollständig, Hintergrund wird daneben sichtbar.'],
                  ['cover', 'Formatfüllend', 'Bild füllt den Rahmen und wird dafür beschnitten.'],
                ] as const
              ).map(([id, label, hint]) => (
                <button
                  key={id}
                  type="button"
                  className={`chip${stage.fit === id ? ' is-active' : ''}`}
                  title={hint}
                  onClick={() => send({ t: 'look:stage', patch: { fit: id } })}
                >
                  {label}
                </button>
              ))}
            </div>

            {stage.backdrop !== 'none' && (
              <>
                <RangeRow
                  label="Weichzeichnen"
                  value={stage.blur}
                  min={0}
                  max={MAX_BACKDROP_BLUR}
                  unit=" px"
                  onChange={(blur) => throttled({ t: 'look:stage', patch: { blur } })}
                />
                <RangeRow
                  label="Abdunkeln"
                  value={stage.dim}
                  min={0}
                  max={MAX_BACKDROP_DIM}
                  onChange={(dim) => throttled({ t: 'look:stage', patch: { dim } })}
                />
              </>
            )}
          </div>

          <div className="deck__block">
            <div className="deck__label">
              Bild pro Kamera
              {selected && <span className="deck__mode">{selected.label}</span>}
            </div>

            {sources.length === 0 ? (
              <div className="empty">Noch keine freigegebene Kamera.</div>
            ) : (
              <>
                <div className="chip-row">
                  {sources.map((source) => (
                    <button
                      key={source.id}
                      type="button"
                      className={`chip${selectedId === source.id ? ' is-active' : ''}`}
                      onClick={() => setPicked(source.id)}
                    >
                      {source.id === activeSourceId ? '● ' : ''}
                      {source.label}
                    </button>
                  ))}
                </div>

                <div className="chip-row">
                  {LOOK_PRESETS.map((preset) => (
                    <button
                      key={preset.id}
                      type="button"
                      className={`chip${look.preset === preset.id ? ' is-active' : ''}`}
                      onClick={() => patchCamera({ preset: preset.id })}
                    >
                      {preset.label}
                    </button>
                  ))}
                  {look.preset === 'custom' && <span className="chip is-active">Eigen</span>}
                </div>

                <RangeRow
                  label="Helligkeit"
                  value={look.brightness}
                  min={LOOK_LIMITS.brightness[0]}
                  max={LOOK_LIMITS.brightness[1]}
                  onChange={(brightness) => patchCamera({ brightness }, true)}
                />
                <RangeRow
                  label="Kontrast"
                  value={look.contrast}
                  min={LOOK_LIMITS.contrast[0]}
                  max={LOOK_LIMITS.contrast[1]}
                  onChange={(contrast) => patchCamera({ contrast }, true)}
                />
                <RangeRow
                  label="Farbe"
                  value={look.saturation}
                  min={LOOK_LIMITS.saturation[0]}
                  max={LOOK_LIMITS.saturation[1]}
                  onChange={(saturation) => patchCamera({ saturation }, true)}
                />
                <RangeRow
                  label="Stimmung"
                  value={look.warmth}
                  min={LOOK_LIMITS.warmth[0]}
                  max={LOOK_LIMITS.warmth[1]}
                  unit=""
                  hint="negativ kühler, positiv wärmer"
                  onChange={(warmth) => patchCamera({ warmth }, true)}
                />

                <div className="row gap-8" style={{ flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className={`btn btn--sm${look.mirror ? '' : ' btn--onstage'}`}
                    title="Für Frontkameras, die verkehrt herum wirken"
                    onClick={() => patchCamera({ mirror: !look.mirror })}
                  >
                    {/* Haken statt „an“/„aus“: der Zustand steckt schon im
                        Aussehen des Knopfes, ein Wort daneben widerspricht ihm
                        nur zu leicht. */}
                    Spiegeln{look.mirror ? ' ✓' : ''}
                  </button>
                  <button type="button" className="btn btn--sm btn--onstage" onClick={() => patchCamera({ preset: 'neutral' })}>
                    Zurücksetzen
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function RangeRow({
  label,
  value,
  min,
  max,
  unit = ' %',
  hint,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  unit?: string;
  hint?: string;
  onChange: (value: number) => void;
}) {
  // Woher der Verlauf der Spur seinen Knick nimmt -- das Stylesheet kann den
  // Wert eines Reglers nicht selbst lesen.
  const fill = ((value - min) / (max - min)) * 100;

  return (
    <label className="deck-slider">
      <span title={hint}>{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={1}
        value={value}
        style={{ ['--fill' as string]: `${fill}%` }}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <b>
        {value}
        {unit}
      </b>
    </label>
  );
}

/**
 * Begrenzt, wie oft ein Schieberegler senden darf.
 *
 * Ein Zug ueber die Skala loest sonst dutzende Nachrichten aus, und jede geht
 * an *alle* im Raum. Die erste geht sofort raus, danach hoechstens alle
 * `ms` -- und der letzte Wert immer, damit der Regler nicht auf einem
 * Zwischenstand stehen bleibt.
 */
function useThrottledSend(send: (msg: ClientMessage) => void, ms = 150) {
  const pending = useRef<ClientMessage | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return useCallback(
    (msg: ClientMessage) => {
      pending.current = msg;
      if (timer.current !== undefined) return;
      const flush = () => {
        const next = pending.current;
        pending.current = null;
        if (!next) {
          timer.current = undefined;
          return;
        }
        send(next);
        timer.current = window.setTimeout(flush, ms);
      };
      flush();
    },
    [send, ms],
  );
}
